<template>
  <content-subpage
    class="acp-html5-interactive-generation-subpage"
    :visible="visible"
    :titles="titles"
    :content-title="t('acp_html5_generation_content_subpage_title')"
    @content-subpage-hidden="handleCloseSubpage"
  >
    <template #main-content>
      <overlay-loader v-if="isLoading" />
      <template v-else>
        <!-- Recipe Selection -->
        <cv-form class="suggested-roles" @submit.prevent>
          <div v-safe-html="recipeSelectionDescription" class="description"></div>

          <!-- Error Banner -->
          <cv-inline-notification
            v-if="showErrorBanner"
            :kind="
              getErrorBannerInfo(INTEGRATOR_APP_FEATURE_CATEGORY.ACP_HTML5, generationErrorCode)
                .bannerKind
            "
            :title="
              t(
                getErrorBannerInfo(INTEGRATOR_APP_FEATURE_CATEGORY.ACP_HTML5, generationErrorCode)
                  .titleKey,
                { feature_name: errorBannerFeatureName },
              )
            "
            low-contrast
            hide-close-button
          >
            <template #subtitle>
              <div>
                {{
                  t(
                    getErrorBannerInfo(
                      INTEGRATOR_APP_FEATURE_CATEGORY.ACP_HTML5,
                      generationErrorCode,
                    ).subtitleKey,
                    { feature_name: errorBannerFeatureName },
                  )
                }}
              </div>
            </template>
          </cv-inline-notification>

          <!-- No recipes empty state -->
          <empty-placeholder v-if="recipes.length === 0">
            <template #icon>
              <GenericEmpty />
            </template>
            <div v-safe-html="t('acp_generation_content_subpage_no_recipe_message')"></div>
          </empty-placeholder>

          <!-- Recipe Carousel -->
          <carousel v-else :start-index="carouselStartIndex">
            <carousel-item v-for="(recipe, index) in recipes" :key="index">
              <recipe-card
                :id="recipe.id"
                class="acp-html5-interactive-generation"
                icon="Roles32"
                :title="recipe.title"
                :content="recipe.content"
                :is-selected="selectedRecipeId === recipe.id"
                @on-click="handleRecipeClick"
              />
            </carousel-item>
          </carousel>

          <!-- Instructions Input -->
          <dl class="field-set type-text acp-html5-interactive-instruction">
            <div class="info">
              {{ t("acp_generation_content_subpage_instructions_message") }}
            </div>
            <dd class="field-input">
              <rich-text-editor
                ref="instructionsEditorComponent"
                size="extra_large"
                kind="plaintext"
                :placeholder="t('acp_html5_generation_content_subpage_instruction_placeholder')"
                :is-mandatory="false"
                :initial-content="instructionsInputValue"
                :content="getCurrentlySelectedRecipeContent()"
                @change="handleInstructionsInput"
              />
            </dd>
          </dl>
        </cv-form>

        <!-- Knowledge Base -->
        <cv-form class="knowledge-base" @submit.prevent>
          <authoring-co-pilot-knowledge-base
            ref="knowledgeBaseRef"
            :header-description="
              t('acp_html5_generation_content_subpage_knowledge_base_description')
            "
            resource-type="LESSON"
            :resource-id="lessonId"
            :is-all-knowledge-base-selected="isAllKnowledgeBaseSelected"
            :selected-knowledge-base-ids="selectedKnowledgeBaseIds"
            :show-exceed-files-error="showExceedFilesError"
            :feature-type="INTEGRATOR_APP_FEATURE_CATEGORY.ACP_HTML5"
            :kb-validation-result="kbValidationResult"
            @knowledge-base-selection="handleKnowledgeBaseSelection"
            @child-dirty-changed="handleChildDirtyChanged"
          />
        </cv-form>
      </template>
    </template>

    <template v-if="!isLoading" #action-button>
      <fetch-data-button
        class="btn-generate"
        kind="ai"
        :tip-position="generateButton.tipPosition"
        :tip-alignment="generateButton.tipAlignment"
        :icon-only="generateButton.iconOnly"
        :disabled="isProcessing"
        :error="generateButton.error"
        :error-message="generateButton.errorMessage"
        :loading="generateButton.loading"
        :completed="generateButton.completed"
        @click="handleGenerateInteractive"
        @reset-completed-state="handleGenerateButtonResetCompleted"
        @reset-error-state="handleGenerateButtonResetError"
      >
        <template v-if="generateButton.error">
          <span>{{ t("button_state_error") }}</span>
        </template>
        <template v-else>
          <svg-icon :name="generateButton.iconName" kind="action" />
          <span>{{ t("acp_generation_content_subpage_generate_button_label") }}</span>
        </template>
      </fetch-data-button>
    </template>
  </content-subpage>
</template>

<script setup lang="ts">
import { onMounted, useTemplateRef } from "vue";
import RichTextContextMixin from "@modules/editor/mixin/richtext.context.mixin";
import AuthoringCoPilotMixin from "@modules/editor/mixin/authoring.copilot.mixin.ts";
import GenericEmpty from "@images/placeholder/empty-states/GenericEmpty.svg?component";
import RecipeCard from "@plugins/recipe/components/RecipeCard.vue";
import AuthoringCoPilotKnowledgeBase from "@modules/editor/components/authoring-copilot/AuthoringCoPilotKnowledgeBase.vue";
import appManagementApi from "@apis/app.management.api.ts";
import { isEmpty } from "@utils/var.check.ts";
import {
  ACP_HTML5_APP_FEATURE_TYPE,
  ACP_REQUEST_STATUS,
  ACP_HTML5_TYPE,
  INTEGRATOR_APP_FEATURE_CATEGORY,
} from "@modules/app/service/app.constant.ts";
import { NAMESPACE } from "@modules/editor/service/editor.constant.ts";
import authoringCopilotApi from "@apis/authoring.copilot.api.ts";
import AcpHtml5InteractiveIterationContentSubpage from "@plugins/authoring-copilot/components/AcpHtml5InteractiveIterationContentSubpage.vue";
import appConfigs from "@configs/app.ts";
import richTextUtils from "@plugins/richtext/utils/richtext.utils.ts";
import { ref, computed } from "vue";
import store from "@plugins/store.ts";
import { useErrorHandler } from "@plugins/common/composables/error.ts";
import { useRecipe } from "@plugins/recipe/recipe-composable.ts";
import { useVm } from "@utils/vm.ts";
import { useProcessingState } from "@modules/app/composables/processing-state.ts";
import { useContentSubpageInstance } from "@plugins/content-subpage/composables/content-subpage-instance.ts";
import { useToast } from "@plugins/toast/composables/toast-instance.ts";
import { useI18n } from "vue-i18n";
import { useAcp } from "@plugins/authoring-copilot/authoring-copilot-composable.ts";
import { useApp } from "@plugins/common/composables/app-composable.ts";
import { useErrorBanner } from "@utils/use-error-banner.ts";
import { HTTP_ERROR_CATEGORY } from "@utils/http.error.ts";
import { useDirty } from "@plugins/common/composables/page-dirty.ts";
import { Recipe } from "@plugins/recipe/types.ts";
import {
  AcpHtml5GenerationParams,
  GenerateButtonState,
  Html5ListItem,
} from "@plugins/authoring-copilot/types.ts";

interface Props {
  params: AcpHtml5GenerationParams;
}

// Props
const props = defineProps<Props>();

defineOptions({
  mixins: [RichTextContextMixin, AuthoringCoPilotMixin],
});

const emit = defineEmits<{
  html5MoCreated: [data: unknown];
}>();
const { vm } = useVm();
const { t } = useI18n();
const { passiveApiFailHandler } = useErrorHandler();
const { getStartIndex, sortRecipesByAlphabeticalOrder, setLastUsedRecipeToLocalStorage } =
  useRecipe();
const { resetProcessing, isProcessing } = useProcessingState("generateNewInteractive");
const contentSubpageInstance = useContentSubpageInstance();
const { visible, titles, actionHidden, pollForStatus, defaultExpose } = useAcp({
  contentSubpageParams: props.params,
  waitModalProcessingTitle: t("acp_html5_generation_wait_modal_title"),
  waitModalProcessingContent: t("acp_html5_generation_wait_modal_content"),
});
const { isAcpHtml5AppAvailable, getAppFeatureName } = useApp();
const { getErrorBannerInfo } = useErrorBanner();
const errorBannerFeatureName = computed(() =>
  getAppFeatureName(INTEGRATOR_APP_FEATURE_CATEGORY.ACP_HTML5),
);
const toast = useToast();
const hasDirty = ref(false);
const dirtyId = ref("createNewInteractive");
const { setDirty, removeDirty } = useDirty(dirtyId);
const knowledgeBaseRef =
  useTemplateRef<InstanceType<typeof AuthoringCoPilotKnowledgeBase>>("knowledgeBaseRef");
const instructionsEditorComponent = useTemplateRef<HTMLElement>("instructionsEditorComponent");
defineExpose(defaultExpose);

// Data
const recipes = ref<Recipe[]>([]);
const isLoading = ref(false);
const instructionsInputValue = ref("");
const selectedRecipeId = ref<number | null>(null);
const hasUpdatedInstructionValue = ref(false);
const selectedKnowledgeBaseIds = ref<number[]>([]);
const isAllKnowledgeBaseSelected = ref(false);
const showErrorBanner = ref(false);
const generationErrorCode = ref<string | undefined>(undefined);
const generateButton = ref<GenerateButtonState>({
  kind: "ai",
  size: "field",
  completed: false,
  error: false,
  loading: false,
  iconOnly: false,
  tipPosition: "top",
  tipAlignment: "end",
  errorMessage: "",
  iconName: "ACPGenerate24",
});
const requestId = ref("");
const maxInstructionLength = ref(30000);
const htmlMoCreated = ref(false);
const carouselStartIndex = ref(0);

// Computed
const userId = computed(() => store.getters["auth/userId"]);

const appFeatureId = computed(() => {
  const appFeatures = store.getters["app/getIntegratorAppAccessByCategory"](
    INTEGRATOR_APP_FEATURE_CATEGORY.ACP_HTML5,
  );
  const acpHtml5GenerateAppFeature = appFeatures.find(
    (appFeature) => appFeature.featureType === ACP_HTML5_APP_FEATURE_TYPE.ACP_HTML5_ANTHROPIC,
  );
  return acpHtml5GenerateAppFeature ? parseInt(acpHtml5GenerateAppFeature.appFeatureId) : null;
});
const lessonId = computed(() => store.getters[`${NAMESPACE.LESSON}/getLessonId`]);
const lessonTitle = computed(() => store.getters[`${NAMESPACE.LESSON}/getLessonTitle`]);

const recipeSelectionDescription = computed(() => {
  return t("acp_html5_generation_content_subpage_recipe_selection_description", {
    copyrightWorksUrl: appConfigs.copyrightResourcesUrl,
    acpHtml5InteractiveGuideUrl: appConfigs.acpHtml5InteractiveGuideUrl,
  });
});

// Methods
const initializeSubpage = async function () {
  isLoading.value = true;
  try {
    await fetchRecipes();
  } catch (error) {
    passiveApiFailHandler(error);
  } finally {
    isLoading.value = false;
  }
};

const fetchRecipes = async function () {
  if (!isAcpHtml5AppAvailable.value) {
    return;
  }

  try {
    const response = await appManagementApi.getIntegratorAppFeatureRecipe(appFeatureId.value);
    if (response.code !== "ok") {
      throw new Error("Failed to fetch recipes");
    }
    recipes.value = sortRecipesByAlphabeticalOrder(response.data);
    carouselStartIndex.value = getStartIndex(recipes.value);
  } catch (error) {
    passiveApiFailHandler(error);
    throw error;
  }
};

const getCurrentlySelectedRecipeContent = function () {
  if (recipes.value.length === 0 || selectedRecipeId.value === null) {
    if (!hasUpdatedInstructionValue.value) {
      return "";
    }
    hasUpdatedInstructionValue.value = false;
    return instructionsInputValue.value;
  }

  hasUpdatedInstructionValue.value = false;
  return recipes.value.find((r) => r.id === selectedRecipeId.value)?.content || "";
};

const handleRecipeClick = function (event: { id: number }): void {
  if (selectedRecipeId.value === event.id) {
    // Unselect if already selected
    if (!hasUpdatedInstructionValue.value) {
      resetInstructionInputValue();
    }
    selectedRecipeId.value = null;
  } else {
    selectedRecipeId.value = event.id;
    updateInstructionInputValue(selectedRecipeId.value);
  }
  handleSetDirty();
};

const handleInstructionsInput = function (value: string): void {
  instructionsInputValue.value = value;
  if (selectedRecipeId.value) {
    hasUpdatedInstructionValue.value = true;
  }
  handleSetDirty();
};

const updateInstructionInputValue = function (recipeId: number): void {
  const recipe = recipes.value.find((r) => r.id === recipeId);
  if (recipe) {
    instructionsInputValue.value = recipe.content;
    handleSetDirty();
  }
};

const resetInstructionInputValue = function () {
  instructionsInputValue.value = "";
  handleSetDirty();
};

const validateInstructions = function (): boolean {
  const cleanedContent = richTextUtils.removeMediaAndStripHTML(instructionsInputValue.value);
  return cleanedContent.length <= maxInstructionLength.value;
};

const handleGenerateInteractive = async function () {
  let canProceed: boolean;
  try {
    canProceed = knowledgeBaseRef.value
      ? await knowledgeBaseRef.value.resetEditingAndExpandedStates()
      : true;
  } catch (e) {
    passiveApiFailHandler(e);
    return;
  }

  if (!canProceed) {
    return;
  }

  generateButton.value.loading = true;
  resetProcessing();
  dismissErrorBanner();

  try {
    // Check app permission
    if (!isAcpHtml5AppAvailable.value) {
      handleGenerateButtonError();
      return;
    }

    // Validate instructions
    if (!validateInstructions()) {
      handleGenerateButtonError();
      return;
    }

    const selectedKnowledgeBases = knowledgeBaseRef.value?.selectedKnowledgeBases || [];

    // Validate selected knowledge bases - using mixin method
    if (
      !vm.validateSelectedKnowledgeBase(
        selectedKnowledgeBases,
        INTEGRATOR_APP_FEATURE_CATEGORY.ACP_HTML5,
      )
    ) {
      generateButton.value.loading = false;
      return;
    }

    // Prepare request data
    const requestData = getGenerateInteractiveRequestData();

    // Save last used recipe
    setLastUsedRecipeToLocalStorage(userId.value, selectedRecipeId.value);

    const response = await authoringCopilotApi.generateAcpHtml5Interactive(requestData);

    if (response.code !== "ok" || !response.data?.id) {
      showErrorBanner.value = true;
      generationErrorCode.value = response.errorMap?.[0];
      throw new Error("Failed to generate interactive");
    }

    // Store request ID for polling
    requestId.value = response.data.id;

    try {
      const pollResult = await pollForStatus(
        requestId.value,
        authoringCopilotApi.getAcpHtml5InteractiveStatus,
      );
      handleGenerationComplete(pollResult);
    } catch (error) {
      handleGenerateButtonError();
      passiveApiFailHandler(error);
    }
  } catch (error) {
    handleGenerateButtonError();
    passiveApiFailHandler(error);
  }
};

const handleGenerationComplete = function (pollResult) {
  handleRemoveDirty();
  const status = pollResult?.data?.status;

  if (status === ACP_REQUEST_STATUS.SUCCESS) {
    const fileUrl = `${appConfigs.s3ResourceDomain}/authoring-copilot/html5/${requestId.value}/index.html`;

    const firstHtml5ListItem: Html5ListItem = {
      id: requestId.value,
      fileUrl: fileUrl,
      type: ACP_HTML5_TYPE.DRAFT,
    };

    handleGenerateSuccess();
    openIterationSubpage([firstHtml5ListItem]);
  } else if (
    status === ACP_REQUEST_STATUS.NO_RESULT ||
    status === ACP_REQUEST_STATUS.ERROR ||
    status === ACP_REQUEST_STATUS.FILE_VALIDATION_FAILED
  ) {
    if (status === ACP_REQUEST_STATUS.FILE_VALIDATION_FAILED) {
      vm.showKnowledgeBaseFileValidationError();
    }
    // NO_RESULT means the LLM replied but the payload failed SLS validation,
    // so the lambda does not send an errorCode for it
    generationErrorCode.value =
      status === ACP_REQUEST_STATUS.NO_RESULT
        ? HTTP_ERROR_CATEGORY.INVALID_RESPONSE
        : pollResult?.data?.errorCode;
    // Error
    showErrorBanner.value = true;
    handleGenerateButtonError();
  }
};

const openIterationSubpage = function (html5List: Html5ListItem[]) {
  contentSubpageInstance.show(
    AcpHtml5InteractiveIterationContentSubpage,
    {
      baseTitle: [...titles.value],
      richTextContext: props.params.richTextContext,
      html5List: html5List,
      requestId: requestId.value,
      hasParentSubpage: true,
    },
    {
      html5MoCreated: (data) => {
        htmlMoCreated.value = true;
        emit("html5MoCreated", data);
        showAddInteractiveSuccessToast();
      },
    },
  );
};

const showAddInteractiveSuccessToast = function () {
  toast.show("success", {
    title: t("acp_html5_interactive_add_interactive_success_toast_title"),
  });
};

const getGenerateInteractiveRequestData = function () {
  return {
    rt_instruction: instructionsInputValue.value.trim(),
    knowledgeBaseIds: selectedKnowledgeBaseIds.value,
  };
};

const handleGenerateSuccess = function () {
  resetProcessing();
  generateButton.value.completed = true;
  generateButton.value.loading = false;
};

const handleGenerateButtonError = function () {
  generateButton.value.loading = false;
  generateButton.value.error = true;
  resetProcessing();
};

const handleGenerateButtonResetCompleted = function () {
  generateButton.value.completed = false;
};

const handleGenerateButtonResetError = function () {
  generateButton.value.error = false;
  generateButton.value.errorMessage = "";
};

const dismissErrorBanner = function () {
  showErrorBanner.value = false;
  generationErrorCode.value = undefined;
};

const handleKnowledgeBaseSelection = function (data): void {
  const newSelectedKnowledgeBaseIds = data.selectedKnowledgeBases
    .map((item) => item.id)
    .filter((id) => !isEmpty(id));
  const allKnowledgeBaseIds = data.knowledgeBase
    .map((item) => item.id)
    .filter((id) => !isEmpty(id));

  const currentSaveIds = !isAllKnowledgeBaseSelected.value
    ? selectedKnowledgeBaseIds.value.filter((id) => allKnowledgeBaseIds.includes(id))
    : allKnowledgeBaseIds;

  isAllKnowledgeBaseSelected.value = isEmpty(allKnowledgeBaseIds)
    ? false
    : isArrayEqual(newSelectedKnowledgeBaseIds, allKnowledgeBaseIds);

  if (!isArrayEqual(currentSaveIds, newSelectedKnowledgeBaseIds)) {
    handleSetDirty();
  }

  if (isEmpty(allKnowledgeBaseIds)) {
    handleRemoveDirty();
  }

  selectedKnowledgeBaseIds.value = isAllKnowledgeBaseSelected.value
    ? allKnowledgeBaseIds
    : newSelectedKnowledgeBaseIds;
};

const isArrayEqual = function (arr1: number[], arr2: number[]): boolean {
  if (arr1.length !== arr2.length) return false;
  return arr1.slice().sort().toString() === arr2.slice().sort().toString();
};

const handleCloseSubpage = function () {
  handleRemoveDirty();
  actionHidden();
};

const handleChildDirtyChanged = function (isDirty: boolean) {
  if (isDirty) {
    handleSetDirty();
  }
};

const handleSetDirty = function () {
  hasDirty.value = true;
  setDirty(dirtyId.value);
};

const handleRemoveDirty = function () {
  hasDirty.value = false;
  removeDirty(dirtyId.value);
};

onMounted(async () => {
  titles.value.push(lessonTitle.value);
  titles.value.push(t("acp_html5_generation_content_subpage_title"));
  await initializeSubpage();
});
</script>

<style lang="scss">
@import "@styles/layout/breakpoint";
@import "@styles/themes/sls-theme/tokens/colors";
@import "@styles/themes/sls-theme/tokens/spacing";
@import "@styles/themes/sls-theme/tokens/typo";

.acp-html5-interactive-generation-subpage {
  .main {
    .main-header {
      margin-bottom: $spacing-02;
    }
  }

  .info {
    @include carbon--type-style("body-short-01");
    margin-bottom: $spacing-03;
    color: $text-02;
    white-space: pre-line;
  }

  .empty-content {
    text-align: center;

    h4 {
      @include carbon--type-style("productive-heading-02", true);
      margin-bottom: $spacing-03;
      color: $text-01;
    }

    p {
      @include carbon--type-style("body-long-01", true);
      color: $text-02;
    }
  }

  .suggested-roles {
    margin-bottom: $spacing-06;

    .description {
      @include carbon--type-style("body-long-01", true);
      color: $text-01;
    }

    .empty-placeholder {
      margin: $spacing-06 0 $spacing-05;

      .content {
        margin-top: 0;
      }
    }

    .acp-html5-interactive-generation-instruction {
      margin-top: $spacing-05;

      .info {
        @include carbon--type-style("helper-text-01");
      }
    }
  }

  .acp-html5-interactive-instruction {
    .field-input {
      .mce-content-body {
        min-height: 155px;
      }
    }
  }

  .knowledge-base {
    .authoring-copilot-knowledge-base {
      // increase bottom spacing to allow dropdown to be visible in content subpage
      padding-bottom: 8rem;
    }

    .info {
      margin-bottom: $spacing-06;
      white-space: pre-line;
    }
  }

  .knowledge-base-listing {
    .add-knowledge-base {
      margin-top: $spacing-07;

      .field-input {
        display: flex;
        justify-content: center;
      }
    }
  }

  .mce-content-body {
    &[data-mce-placeholder] {
      white-space: pre-line;
    }
  }

  // Error banner
  .bx--inline-notification--error {
    margin-bottom: $spacing-05;
  }
}
</style>
